import { useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, getImageUrl } from '@/lib/api'
import toast from 'react-hot-toast'
import { PlusIcon, PencilIcon, TrashIcon, StarIcon, PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline'

interface Level {
  _id: string
  number: number
  title: string
  description?: string
  image?: string
  isPremium: boolean
  isActive: boolean
  lessonsCount: number
  order: number
}

export default function LevelsPage() {
  const queryClient = useQueryClient()
  const [editingLevel, setEditingLevel] = useState<Level | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [uploadedImage, setUploadedImage] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  
  const { data: levels, isLoading } = useQuery({
    queryKey: ['levels'],
    queryFn: async () => {
      const res = await api.get<{ data: { levels: Level[] } }>('/admin/levels')
      return res.data.data.levels
    },
  })
  
  const createMutation = useMutation({
    mutationFn: async (data: Partial<Level>) => {
      await api.post('/admin/levels', data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['levels'] })
      toast.success('Уровень создан')
      setIsModalOpen(false)
      setUploadedImage(null)
    },
    onError: () => toast.error('Ошибка создания'),
  })
  
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Level> }) => {
      await api.patch(`/admin/levels/${id}`, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['levels'] })
      toast.success('Уровень обновлён')
      setEditingLevel(null)
      setIsModalOpen(false)
      setUploadedImage(null)
    },
    onError: () => toast.error('Ошибка обновления'),
  })
  
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/admin/levels/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['levels'] })
      toast.success('Уровень удалён')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Ошибка удаления')
    },
  })
  
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const data = {
      number: parseInt(formData.get('number') as string),
      title: formData.get('title') as string,
      description: formData.get('description') as string,
      image: uploadedImage || (formData.get('image') as string) || '',
      isPremium: formData.get('isPremium') === 'on',
      isActive: formData.get('isActive') === 'on',
    }
    
    if (editingLevel) {
      updateMutation.mutate({ id: editingLevel._id, data })
    } else {
      createMutation.mutate(data)
    }
  }

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      toast.error('Пожалуйста, выберите изображение')
      return
    }

    const formData = new FormData()
    formData.append('file', file)

    setIsUploading(true)
    try {
      const res = await api.post<{ data: { url: string } }>('/admin/uploads/single?folder=images', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      setUploadedImage(res.data.data.url)
      toast.success('Изображение загружено')
    } catch (error) {
      toast.error('Ошибка загрузки изображения')
    } finally {
      setIsUploading(false)
    }
  }

  const handleRemoveImage = () => {
    setUploadedImage(null)
    setEditingLevel(prev => (prev ? { ...prev, image: '' } : prev))
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }
  
  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Уровни</h1>
        <button
          onClick={() => {
            setEditingLevel(null)
            setUploadedImage(null)
            setIsModalOpen(true)
          }}
          className="btn-primary"
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          Добавить уровень
        </button>
      </div>
      
      {/* Levels grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {isLoading ? (
          <div className="col-span-full text-center py-8 text-gray-400">
            Загрузка...
          </div>
        ) : levels?.length === 0 ? (
          <div className="col-span-full text-center py-8 text-gray-400">
            Уровни не найдены
          </div>
        ) : (
          levels?.map((level) => (
            <div
              key={level._id}
              className={`card ${!level.isActive ? 'opacity-50' : ''}`}
            >
              {level.image ? (
                <div className="mb-4 h-36 w-full overflow-hidden rounded-xl bg-gray-700">
                  <img
                    src={getImageUrl(level.image)}
                    alt={level.title}
                    className="h-full w-full object-cover"
                  />
                </div>
              ) : (
                <div className="mb-4 h-36 w-full rounded-xl bg-gray-700 flex items-center justify-center text-gray-500">
                  <PhotoIcon className="w-8 h-8" />
                </div>
              )}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-3xl font-bold text-white">{level.number}</span>
                  {level.isPremium && (
                    <StarIcon className="w-5 h-5 text-yellow-400" />
                  )}
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => {
                      setEditingLevel(level)
                      setUploadedImage(null)
                      setIsModalOpen(true)
                    }}
                    className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => {
                      if (confirm('Удалить уровень?')) {
                        deleteMutation.mutate(level._id)
                      }
                    }}
                    className="p-2 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <h3 className="font-medium text-white">{level.title}</h3>
              {level.description && (
                <p className="text-sm text-gray-400 mt-1 line-clamp-2">
                  {level.description}
                </p>
              )}
              
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700">
                <span className="text-sm text-gray-400">
                  {level.lessonsCount} уроков
                </span>
                <span className={level.isActive ? 'badge-green' : 'badge-gray'}>
                  {level.isActive ? 'Активен' : 'Скрыт'}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
      
      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-700">
              <h2 className="text-xl font-bold text-white">
                {editingLevel ? 'Редактировать уровень' : 'Новый уровень'}
              </h2>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Номер уровня
                  </label>
                  <input
                    type="number"
                    name="number"
                    required
                    min="1"
                    defaultValue={editingLevel?.number}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    name="title"
                    required
                    defaultValue={editingLevel?.title}
                    className="w-full"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  rows={2}
                  defaultValue={editingLevel?.description}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Картинка уровня
                </label>

                {(uploadedImage || editingLevel?.image) && (
                  <div className="relative mb-3 inline-block">
                    <img
                      src={uploadedImage ? getImageUrl(uploadedImage) : getImageUrl(editingLevel?.image)}
                      alt="Preview"
                      className="h-32 rounded-lg object-cover"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute -top-2 -right-2 p-1 bg-red-500 hover:bg-red-600 rounded-full text-white transition-colors"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                )}

                <div className="flex gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    id="level-image-upload"
                  />
                  <label
                    htmlFor="level-image-upload"
                    className={`btn-secondary cursor-pointer ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <PhotoIcon className="w-5 h-5 mr-2" />
                    {isUploading ? 'Загрузка...' : (uploadedImage || editingLevel?.image ? 'Изменить картинку' : 'Загрузить картинку')}
                  </label>
                </div>

                <input
                  type="hidden"
                  name="image"
                  value={uploadedImage || editingLevel?.image || ''}
                />
              </div>
              
              <div className="flex gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="isPremium"
                    defaultChecked={editingLevel?.isPremium}
                    className="w-4 h-4 rounded"
                  />
                  <span className="text-sm text-gray-300">Premium</span>
                </label>
                
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="isActive"
                    defaultChecked={editingLevel?.isActive ?? true}
                    className="w-4 h-4 rounded"
                  />
                  <span className="text-sm text-gray-300">Активен</span>
                </label>
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false)
                    setEditingLevel(null)
                    setUploadedImage(null)
                  }}
                  className="btn-secondary"
                >
                  Отмена
                </button>
                <button type="submit" className="btn-primary">
                  {editingLevel ? 'Сохранить' : 'Создать'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
